import { FC, useCallback, useEffect, useState } from "react";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../constants/app";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { usePrevious } from "../../../hooks/usePrevious";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  getDateParamsForAPI,
  isSameDateRange
} from "../../../store/timesheet/parser";
import { getApprovalTimesheetThunk } from "../../../store/timesheet/thunks";
import { ITimesheetData, TTimesheetSort } from "../../../store/timesheet/types";
import { SortOrder } from "../../../store/vacancy/types";
import PaginationControls from "../../Pagination/PaginationControls";
import PopupModal from "../../UI/Popup";
import SearchAndFilter from "./common/SearchAndFilter";
import TableHeader from "./common/TableHeader";
import TableRow from "./common/TableRow";
import TabsHeader from "./common/TabsHeader";
import ViewTimesheet from "./common/ViewTimesheet";
import TimeSheetAlert from "./TimeSheetAlert";
const TimesheetApproval: FC = () => {
  const dispatch = useDispatch();

  const { inProgress, approvalTimesheets, approvalPagination, totalOverDueTimesheetCount, totalPendingTimesheetCount } = useSelector(
    (state) => state.timesheet
  );

  const [selectedTimesheet, setSelectedTimesheet] =
    useState<ITimesheetData | null>(null);
  const [showViewTimesheetModal, setShowViewTimesheetModal] =
    useState<boolean>(false);

  // Local states for sorting
  const [sortBy, setSortBy] = useState<TTimesheetSort>("first_name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  // Search and debouncing states
  const [keyword, setKeyword] = useState("");
  const [selectedCentreIds, setSelectedCentreIds] = useState<number[]>([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>(
    []
  );
  const [dateRange, setDateRange] = useState<[Date | null, Date | null] | null>(
    null
  );
  const [searchInput, setSearchInput] = useState<string>("");
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  // Pagination states
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const currentParams = {
    keyword,
    dateRange,
    selectedCentreIds,
    selectedCandidateIds,
    page,
    perPage,
    sortBy,
    sortOrder,
  };
  const previousParams = usePrevious(currentParams);

  // Handle sorting logic
  const handleSortChange = (field: TTimesheetSort) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Handle pagination link click
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setPage(Number(page));
    }
  };

  const handleViewTimesheet = (timesheet: ITimesheetData) => {
    setSelectedTimesheet(timesheet);
    setShowViewTimesheetModal(true);
  };

  const onClickClose = () => {
    setSelectedTimesheet(null);
    setShowViewTimesheetModal(false);
  };

  const fetchApprovalsTImesheets = useCallback(async () => {
    const prev = previousParams;

    const areSame =
      prev &&
      prev.keyword === currentParams.keyword &&
      prev.page === currentParams.page &&
      prev.perPage === currentParams.perPage &&
      prev.sortBy === currentParams.sortBy &&
      prev.sortOrder === currentParams.sortOrder &&
      prev.selectedCentreIds === currentParams.selectedCentreIds &&
      prev.selectedCandidateIds === currentParams.selectedCandidateIds &&
      isSameDateRange(prev.dateRange, dateRange);

    if (areSame) {
      return; // Skip if no actual change
    }

    dispatch(showTransparentPreloaderAction());
    const { start_date, end_date } = getDateParamsForAPI(dateRange);

    try {
      // Now, make the timesheet request
      await dispatch(
        getApprovalTimesheetThunk({
          searchValue: currentParams.keyword,
          page: currentParams.page,
          per_page: currentParams.perPage,
          sort_by: currentParams.sortBy,
          sort_order: currentParams.sortOrder,
          start_date,
          end_date,
          centres: currentParams.selectedCentreIds,
          candidates: currentParams.selectedCandidateIds,
        })
      );
    } finally {
      dispatch(hideTransparentPreloaderAction());
    }
  }, [dispatch, inProgress, currentParams, previousParams]);

  useEffect(() => {
    fetchApprovalsTImesheets();
  }, [
    fetchApprovalsTImesheets,
    keyword,
    dateRange,
    selectedCandidateIds,
    selectedCentreIds,
    page,
    perPage,
    sortBy,
    sortOrder,
  ]);

  // Update debounced keyword and reset page when typing stops
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setKeyword(debouncedSearch);
      setPage(1); // reset to first page on new search
    }
  }, [debouncedSearch]);


  return (
    <>
      <TimeSheetAlert
        totalPending={totalPendingTimesheetCount || 0}
        totalOverdue={totalOverDueTimesheetCount}
        goToLink="/admin/timesheet/payroll-pending"
      />
      <div className="d-flex flex-column gap-20">
        <div className="d-flex flex-column gap-10">
          <div className="d-flex flex-column">
            <div className="d-flex gap-20 justify-content-between overflow-auto mobile-scroll-hide">
              <TabsHeader activeTab="approval" />
            </div>
            <div className="bg-black w-100 h-5px"></div>
          </div>
          <SearchAndFilter
            variant="admin-approval"
            dateRange={dateRange}
            keyword={searchInput}
            selectedCentreIds={selectedCentreIds}
            selectedCandidateIds={selectedCandidateIds}
            onDateRangeChange={setDateRange}
            onKeywordChange={setSearchInput}
            onCentreIdsChange={setSelectedCentreIds}
            onCandidateIdsChange={setSelectedCandidateIds}
          />
          <div className="d-flex flex-column">
            <TableHeader
              variant="admin-approval"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
            {/* <TableRow variant="admin-approval" btnType={"View"} /> */}
            {approvalTimesheets.length > 0 ? (
              approvalTimesheets.map((timesheet, index) => (
                <div key={index}>
                  <TableRow
                    key={timesheet.id}
                    variant="admin-approval"
                    btnType={"View"}
                    timesheet={timesheet}
                    onViewClick={() => handleViewTimesheet(timesheet)}
                  />
                </div>
              ))
            ) : (
              <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
                <div className="fs-16 fw-400 text-grey-medium">
                  No approval timesheets found
                </div>
              </div>
            )}

            {/* Render pagination controls if there are more than 10 users */}
            {approvalPagination &&
              approvalPagination?.total > DEFAULT_ITEMS_PER_PAGE && (
                <PaginationControls
                  currentPage={page} // Current page number
                  totalPages={approvalPagination?.last_page} // Total number of pages
                  paginationLinks={approvalPagination?.links} // List of pagination links
                  onPageChange={handlePageChange} // Function to handle page change
                  setPerPageList={setPerPage} // Function to set the number of items per page
                  setCurrentPage={setPage} // Function to set the current page
                />
              )}
          </div>
        </div>
      </div>
      {/* View Timesheet Modal */}
      <PopupModal
        visible={showViewTimesheetModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
        popUpClass="mobile-model"
      >
        {selectedTimesheet && <ViewTimesheet timesheet={selectedTimesheet} />}
      </PopupModal>
    </>
  );
};

export default TimesheetApproval;
